import React from 'react';
import { ActivityCardProps } from 'core/type/ActivityCard.types';
import { useDispatch, useSelector } from 'react-redux';
import { selectConfigFromBootstrap } from 'store/slice/bootstrap';
import { getDetailUrl } from 'core/utils/activityUrlGenerator';
import { routeTo } from 'store/slice/navigation';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import { ActivitiesPoiListItemClickEventActivitiesFieldsV1 } from '@agoda/analytics-messages-definition-activities/events.split';
import { ActivitiesPoiListItemClickEventActivitiesManifestV1 } from '@agoda/analytics-messages-definition-activities/manifest/events';
import { Box, Divider, Icon, List, ListItemV2, Typography } from '@drone-js/core';
import { StringFormatter } from '@agoda/react-handyman';
import styled from 'styled-components';
import { RecentSearchItem } from 'core/type/SuggestionResult.types';
import { TextSearchType } from 'core/type/TextSearch.types';
import { MAX_RECENT_SEARCH, setRecentlySearch } from 'component/common/TextSearch/suggestionUtils';
import { PriceAmount } from 'component/common/Price/PriceAmount';
import { ExperimentId } from 'core/type/ExperimentId';
import Experiment from 'component/common/Experiment/Experiment';
import { getPriceDecimal } from 'core/utils/roundNumberWithDecimal';
import { MIN_PRICE_FOR_ROUNDING } from 'component/common/Price/Price';

export interface PoiCardDisplayData {
    title: string;
    subTitle: string;
    image: string;
    imageAlt: string;
    activities: ActivityCardProps[];
}

export const PoiListItem: React.FC<{ activity: ActivityCardProps; index: number }> = (props) => {
    const { activity, index } = props;
    const { cms, language, geo, currency, behavior } = useSelector(selectConfigFromBootstrap);
    const dispatch = useDispatch();
    const isSearchBoxFeatureGroup = behavior.featureExperiments.featureBehavior.SearchBoxFeatureGroup;
    const isPriceRoundingUp = behavior.common.price.roundingPriceBehavior;

    if (!activity) return null;

    const allInclusiveChargeTotal = activity.totalPrice?.allInclusive?.chargeTotal || 0;
    const decimal = isPriceRoundingUp ? getPriceDecimal(allInclusiveChargeTotal, MIN_PRICE_FOR_ROUNDING) : 2;

    const onClick = () => {
        if (!activity.id || !language.cultureCode) return;
        const detailPageUrl = getDetailUrl(activity.id, language.cultureCode, geo?.cityId);

        const recentClick: RecentSearchItem = {
            id: activity.id,
            type: TextSearchType.Activity,
            activityName: activity.title,
            url: detailPageUrl,
            isUrlWithPrefix: true,
            cityName: activity.cityName || '',
            cityId: activity.cityId || 0,
            countryName: activity.countryName || '',
            supplierId: undefined,
        };

        isSearchBoxFeatureGroup && setRecentlySearch(recentClick, MAX_RECENT_SEARCH);

        trackElementEvent<ActivitiesPoiListItemClickEventActivitiesFieldsV1>(
            ActivitiesPoiListItemClickEventActivitiesManifestV1.ids,
            {
                activity_id: activity.id,
                supplier_id: activity.supplierId || -1,
            },
            {
                action_element_index: index,
            },
        );

        dispatch(routeTo(detailPageUrl));
    };

    return (
        <Box
            as="button"
            height="100%"
            data-element-name="activities-poi-list-item"
            data-testid="activities-poi-list-item"
            data-element-index={index}
            interactive="product.primary"
            p="16"
            tabIndex={0}
            onClick={onClick}
            textAlign="start"
            width="100%"
        >
            <Typography variant="body-s-strong" pb="4" lineClamp={2}>
                {activity.title}
            </Typography>
            <Experiment.WhenA id={ExperimentId.ACT_4712}>
                <Box display="flex" alignItems="flex-end">
                    {activity.rating > 0 && (
                        <>
                            <Icon id="fill.symbol.star" color="product.rating-hotels" me="4" size="16" />
                            <Typography variant="body-s-strong" me="4">
                                {activity.rating}
                            </Typography>
                        </>
                    )}
                    {activity.totalCount > 0 && (
                        <Typography variant="caption-default" textColor="base.subtle">
                            {StringFormatter.format(cms[269275], activity.totalCount.toLocaleString())}
                        </Typography>
                    )}
                </Box>
            </Experiment.WhenA>
            <Experiment.WhenB id={ExperimentId.ACT_4712}>
                <Box display="flex" justifyContent="space-between">
                    <Box display="flex" alignItems="flex-end" justifyContent="flex-start">
                        {activity.rating > 0 && (
                            <>
                                <Icon id="fill.symbol.star" color="product.rating-hotels" me="4" size="16" />
                                <Typography variant="body-s-strong" me="4">
                                    {activity.rating}
                                </Typography>
                            </>
                        )}
                        {activity.totalCount > 0 && (
                            <Typography variant="caption-default" textColor="base.subtle">
                                {StringFormatter.format(cms[269275], activity.totalCount.toLocaleString())}
                            </Typography>
                        )}
                    </Box>
                    <Box data-testid="poi-item-price" display="flex" alignItems="center" justifyContent="flex-end">
                        <PriceAmount activityId={activity.id} amount={allInclusiveChargeTotal} currency={currency.displayCode} decimal={decimal} />
                    </Box>
                </Box>
            </Experiment.WhenB>
        </Box>
    );
};

PoiListItem.displayName = 'PoiListItem';

const ListItemV2Styled = styled(ListItemV2)`
    padding: 0;
`;

export const PoiListItemContainer: React.FC<{ data: PoiCardDisplayData }> = (props) => {
    const { data } = props;
    return (
        <List m="4" p="0">
            {data.activities.map((activity, index) => (
                <React.Fragment key={index}>
                    <ListItemV2Styled contentSlot={<PoiListItem activity={activity} index={index} />} tabIndex={-1} />
                    {data.activities.length - 1 !== index && (
                        <Box mx="24">
                            <Divider direction="horizontal" />
                        </Box>
                    )}
                </React.Fragment>
            ))}
        </List>
    );
};

PoiListItemContainer.displayName = 'PoiListItemContainer';
